import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import FaqComponent from '../../components/Faq/FaqComponent';
import faqImage1 from '../../client_customizations/assets/images/faqImage1.svg';
import faqImage2 from '../../client_customizations/assets/images/faqImage2.svg';
import faqImage3 from '../../client_customizations/assets/images/faqImage3.svg';
import faqImage4 from '../../client_customizations/assets/images/faqImage4.svg';
import FAQCard from '../../client_customizations/components/FAQCard/FAQCard';
import './FAQ.scss';
import { FAQDATA } from '../../client_customizations/data/faq/FAQDATA.js';

const FAQ = ({ faqs, language }) => {
  useEffect(() => {
    document.title = process.env.REACT_APP_PAGES_FAQ_TITLE;
  });

  const sortedWithSections = FAQDATA[0];

  return (
    <div className="FAQ">
      <section
        className="container white faq-jumbotron-container"
        style={{ padding: '0' }}
      >
        <div className="row">
          <div className="faq-jumbotron">
            <div className="title-container">
              <h1 className="title-faq pad-top">
                <FormattedMessage
                  id="FAQ.title.ev"
                  defaultMessage="EV"
                  description="EV"
                />
                &nbsp;
                <span>
                  <FormattedMessage
                    id="FAQ.title.101"
                    defaultMessage="101"
                    description="101"
                  />
                </span>
              </h1>
              <h2 className="subtitle-faq mb-bottom pad-bottom">
                <FormattedMessage
                  id="FAQ.subtitle"
                  defaultMessage="EVs are still new and it’s natural to have questions. Find answers below."
                  description="EVs are still new and it’s natural to have questions. Find answers below."
                />
              </h2>
            </div>
          </div>
        </div>
      </section>
      <section className="container" style={{ paddingBottom: '0' }}>
        <FAQCard
          title={
            <FormattedMessage
              id="FAQ.sectionTitleOne"
              defaultMessage="What's an EV and how does it work?"
              description="What's an EV and how does it work?"
            />
          }
          body={
            <FormattedMessage
              id="FAQ.bodyOne"
              defaultMessage="An electric vehicle (EV) is a type of vehicle that uses electricity as its primary source of power, rather than gasoline or diesel fuel. EVs have become increasingly popular in recent years due to their potential to lower operating costs and reduce emissions compared to traditional gasoline-powered vehicles.{lineBreak} So, how does an EV work? An EV is powered by an electric motor, which is in turn powered by a battery. The battery stores electricity, which is used to power the motor and turn the wheels of the vehicle. When the vehicle is in use, the battery is slowly depleted. Factors  such as heater or AC use and driving conditions can affect driving range. To recharge the battery, the EV must be plugged into an electric power source, such as a wall outlet or a public charging station.{lineBreak} There are two main types of EVs: battery electric vehicles (BEVs) and plug-in hybrid electric vehicles (PHEVs). BEVs are powered exclusively by electricity, while PHEVs are powered by both an electric motor and a gasoline engine. PHEVs can switch between the two power sources as needed, allowing them to extend their range beyond what is possible with electricity alone."
              description="FAQ.bodyOne"
              values={{
                lineBreak: (
                  <p style={{ margin: '5px' }}>
                    <br />
                  </p>
                ),
              }}
            />
          }
          image={faqImage1}
        />
        <FAQCard
          title={
            <FormattedMessage
              id="FAQ.sectionTitleTwo"
              defaultMessage="EV charging 101"
              description="EV charging 101"
            />
          }
          body={
            <FormattedMessage
              id="FAQ.bodyTwo"
              defaultMessage="EV charging is categorized into three levels: Level 1, Level 2 and Level 3 (DC Fast Charging or Supercharging at Tesla charging stations). About 85% of EV charging occurs at home with Level 1 or Level 2 chargers. {lineBreak} Level 1 charging uses a standard 120-volt household outlet, providing the slowest charge, typically adding 2-5 miles of range per hour for most EVs, making it suitable for overnight charging at home. Fewer miles of range per hour of charging will be added to larger, heavier EVs. {lineBreak} Level 2 charging utilizes a 240-volt outlet like those used for large appliances. It delivers a faster charge, adding about 10-60 miles of range per hour depending on the power output of the charger and the vehicle being charged, and is commonly found in homes, workplaces and public charging stations. Installation of a level 2 charger electrical circuit should be completed by a professional electrician. {lineBreak} Finally, level 3 or DC Fast Charging/Supercharging employs direct current (DC) and high-power stations (usually 400 volts or more), providing the fastest charge. It's capable of adding 60-100 miles  of range in just 20 minutes (depending on power output capacity of the charger) and is primarily used in commercial settings and along highways for quick recharges."
              description="FAQ.bodyTwo"
              values={{
                lineBreak: (
                  <p style={{ margin: '5px' }}>
                    <br />
                  </p>
                ),
              }}
            />
          }
          image={faqImage2}
        />
        <FAQCard
          title={
            <FormattedMessage
              id="FAQ.sectionTitleThree"
              defaultMessage="Alleviate EV range anxiety"
              description="Alleviate EV range anxiety"
            />
          }
          body={
            <FormattedMessage
              id="FAQ.bodyThree"
              defaultMessage="EVs vary significantly in how far they can travel on a single charge, depending on factors such as battery capacity, vehicle efficiency and driving conditions. Most modern EVs offer ranges between 200 and 300 miles per charge, with some models exceeding 400 miles. Technological advancements and increasing investments in battery technology are continually pushing these limits, improving energy density and efficiency. {lineBreak} If you do need to charge on the road, finding a public charging station for EVs is becoming easier due to the rapid expansion of charging infrastructure. In urban areas and along major highways, charging stations are relatively common, with networks like Tesla Superchargers, ChargePoint and Electrify America providing widespread coverage. Many EVs come equipped with navigation systems that include charging station locations, and numerous smartphone apps and websites also help drivers locate nearby chargers, check their availability and even start charging sessions remotely. While rural and less populated areas may still have fewer options, the continuous growth of the EV market is driving further investment in charging infrastructure, making it more convenient for EV owners to find public charging stations across diverse locations."
              description="FAQ.bodyThree"
              values={{
                lineBreak: (
                  <p style={{ margin: '5px' }}>
                    <br />
                  </p>
                ),
              }}
            />
          }
          image={faqImage3}
        />
        <FAQCard
          title={
            <FormattedMessage
              id="FAQ.sectionTitleFour"
              defaultMessage="EV benefits: environmental, cost and maintenance"
              description="FAQ.sectionTitleFour"
            />
          }
          body={
            <FormattedMessage
              id="FAQ.bodyFour"
              defaultMessage="One of the main benefits of EVs is their potential to reduce emissions. Because they are powered by electricity which can be produced cleanly, rather than fossil fuels, they produce no tailpipe emissions when in use. This makes them a cleaner alternative to traditional gasoline-powered vehicles. In addition, EVs are typically more energy efficient than gasoline-powered vehicles, which can further reduce their environmental impact. {lineBreak} Another advantage of EVs is their low operating costs. Because they have fewer moving parts than gasoline-powered vehicles, they usually require less maintenance and consequently have longer lifespans. In addition, electricity is generally cheaper than gasoline, so operating an EV can be more cost-effective in the long run. With SMUD's EV rate discount, charging at home is like paying about $1 a gallon of gas. There are also several incentives and discounts available for EV owners, including federal tax credits and state rebates and grants, HOV lane access and reduced tolls. These incentives can further lower the cost of ownership."
              description="FAQ.bodyFour"
              values={{
                lineBreak: (
                  <p style={{ margin: '5px' }}>
                    <br />
                  </p>
                ),
              }}
            />
          }
          image={faqImage4}
        />
      </section>
      <section className="container" style={{ paddingTop: '0' }}>
        <div className="row faqContainerPage">
          {Object.values(sortedWithSections).map((section) => (
            <div key={`faq-${section.title}`}>
              <h2>
                {language === 'EN' ? section.title : section.titleSP}
              </h2>
              <FaqComponent faqs={section.faqs} language={language} />
            </div>
          ))}
          <div className="faq-orange-button-container">
            <a
              href="https://www.smud.org/driveelectric"
              className="faq-orange-button"
              target="_blank"
              rel="noopener noreferrer"
            >
              <FormattedMessage
                id="FAQ.orangeButtonText"
                defaultMessage="Learn more about our electric vehicle programs"
                description="FAQ.orangeButtonTextr"
              />
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FAQ;

FAQ.propTypes = {
  faqs: PropTypes.array,
  language: PropTypes.string,
};
